import dayjs from "dayjs";
import { getUserDetails } from "@/services/api/auth";
import { isProfileSuccess, normalizeUserProfile } from "@/utils/profileHelpers";
import { getAuthToken } from "@/utils/session";

const TRAVEL_SWITCH_PUBLIC_KEY = `-----BEGIN PUBLIC KEY-----
MIICIjANBgkqhkiG9w0BAQEFAAOCAg8AMIICCgKCAgEAyrN+zuwtD7TrpFPLIluw
Yf8cf402FJnhGzXfEnAd2Vg8U1xh9cr7+z7azkKI/2JN8te5DcFuCdr0s2TiEV/s
ss4FRlWWoqhLUdqFbksi9HyIEzkZ0lkYXpc/DQjoPkeeXMMjT3BXWKWY+Y/dI4wx
Ft0uSo/2MrsH7AnUAkwnVg1S6wQhvzTzH+cfwBj5ODMgiz4jVhQdg3OFAquU6H5P
DIxULwBzhJlYtukbyzQIfGrGyiGM+Y1Kb8WBO932vZVqAZ+6SRZ1cp/7POiH80/z
Pmt2xdjpMQPhB2upUW97mxRhNk2pH877HDFUyWoS3H+Zr0xhP0ev8sbvlni98hJB
l6bix0ThULVButqwxc661Sjf7DkcJj+S+BL+p2f130fcsS9IW7p6XsqJGEef0F+1
VtmseS7HeJm/ZU+dVtNpfOncORgXIwy73L1bY6/2K08hZJJc+XAB0J/4ZO2KCI+w
IdujXm56e+6voW0y80br64y9vT61BXIGlNGlyFDOfLypLMCnnfOdsdelnFlTdO5G
q7jmbaMXwnb9IRsOhM4iXn10Z0dwD6mVXhishJTp3/SqC5lViCvLet7qlRMQeL70
fjebfdDlhb2qyT1UgWG/n1o/FQXC09FnW6Wzc59SipiVjtrog7xhDTBuVFWHuj3q
EoQbH794URhSMMxYX8VjHWUCAwEAAQ==
-----END PUBLIC KEY-----`;

function bytesToBase64(bytes) {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

export function appendSsoToUrl(url = "", sso = "") {
  if (!url) return url;
  const [base, hash = ""] = String(url).split("#");
  const hashSuffix = hash ? `#${hash}` : "";

  try {
    const parsed = new URL(base);
    if (sso) {
      parsed.searchParams.set("sso", sso);
    } else {
      parsed.searchParams.delete("sso");
    }
    return `${parsed.toString()}${hashSuffix}`;
  } catch {
    if (!sso) return url;
    const separator = base.includes("?") ? "&" : "?";
    return `${base}${separator}sso=${encodeURIComponent(sso)}${hashSuffix}`;
  }
}

export async function encryptPayload(payload, publicKeyPem) {
  const pemContents = String(publicKeyPem)
    .replace(/-----BEGIN PUBLIC KEY-----/g, "")
    .replace(/-----END PUBLIC KEY-----/g, "")
    .replace(/\s+/g, "");

  const binaryDer = Uint8Array.from(atob(pemContents), (char) =>
    char.charCodeAt(0)
  );

  const publicKey = await crypto.subtle.importKey(
    "spki",
    binaryDer.buffer,
    {
      name: "RSA-OAEP",
      hash: "SHA-256",
    },
    false,
    ["encrypt"]
  );

  const encrypted = await crypto.subtle.encrypt(
    { name: "RSA-OAEP" },
    publicKey,
    new TextEncoder().encode(JSON.stringify(payload))
  );

  return bytesToBase64(new Uint8Array(encrypted));
}

function resolveSsoProfile(userProfile) {
  if (!userProfile || typeof userProfile !== "object") return null;

  const profile = normalizeUserProfile(userProfile);
  if (!profile.emailId && !profile.firstName && !profile.lastName) return null;
  return profile;
}

export async function generateSSO(userProfile) {
  const token = getAuthToken();
  let profile = resolveSsoProfile(userProfile);

  if (!profile && token) {
    const response = await getUserDetails(token);
    if (isProfileSuccess(response)) {
      profile = resolveSsoProfile(response);
    }
  }

  if (!profile?.emailId) {
    throw new Error("Profile not found");
  }

  const payload = {
    email: profile.emailId,
    ts: `${dayjs().format("YYYY-MM-DDTHH:mm:ss")}Z`,
    clientId: "BLUEBERRY",
    env: process.env.NEXT_PUBLIC_ENV === "production" ? "prod" : "uat",
    firstName: profile.firstName || "",
    lastName: profile.lastName || "",
    cc: String(profile.countryCode || "").replace(/^\+/, ""),
    phone: profile.mobileNumber || "",
  };

  return encryptPayload(payload, TRAVEL_SWITCH_PUBLIC_KEY);
}
